import { IconArrowLeft, IconArrowRight } from "@tabler/icons-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCallback, useEffect, useState } from "react";

type Testimonial = {
  quote: string;
  name: string;
  designation: string;
  src: string;
};

export const AnimatedTestimonials = ({
  testimonials,
  autoplay = false,
}: {
  testimonials: Testimonial[];
  autoplay?: boolean;
}) => {
  const [active, setActive] = useState(0);

  const handleNext = useCallback(() => {
    setActive((prev) => (prev + 1) % testimonials.length);
  }, [testimonials.length]);

  const handlePrev = () => {
    setActive((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const isActive = (index: number) => index === active;

  const truncateText = (text: string, maxLength: number) => {
    return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
  };

  useEffect(() => {
    if (autoplay) {
      const interval = setInterval(handleNext, 5000);
      return () => clearInterval(interval);
    }
  }, [autoplay, handleNext]);

  const randomRotateY = () => Math.floor(Math.random() * 21) - 10;

  return (
    <div className="testify-max-w-sm md:testify-max-w-4xl testify-mx-auto testify-antialiased testify-font-sans testify-px-4 md:testify-px-8 lg:testify-px-12 testify-py-20">
      <div className="testify-relative testify-grid testify-grid-cols-1 md:testify-grid-cols-2 testify-gap-20">
        <div>
          <div className="testify-relative testify-h-80 testify-w-full">
            <AnimatePresence>
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.src}
                  initial={{
                    opacity: 0,
                    scale: 0.9,
                    z: -100,
                    rotate: randomRotateY(),
                  }}
                  animate={{
                    opacity: isActive(index) ? 1 : 0.7,
                    scale: isActive(index) ? 1 : 0.95,
                    z: isActive(index) ? 0 : -100,
                    rotate: isActive(index) ? 0 : randomRotateY(),
                    zIndex: isActive(index)
                      ? 999
                      : testimonials.length + 2 - index,
                    y: isActive(index) ? [0, -80, 0] : 0,
                  }}
                  exit={{
                    opacity: 0,
                    scale: 0.9,
                    z: 100,
                    rotate: randomRotateY(),
                  }}
                  transition={{
                    duration: 0.4,
                    ease: "easeInOut",
                  }}
                  className="testify-absolute testify-inset-0 testify-origin-bottom"
                >
                  <img
                    src={testimonial.src}
                    alt={testimonial.name}
                    width={500}
                    height={500}
                    draggable={false}
                    className="testify-h-full testify-w-full testify-rounded-3xl testify-object-cover testify-object-center testify-flex-shrink-0"
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
        <div className="testify-flex testify-justify-between testify-flex-col testify-py-4">
          <motion.div
            key={active}
            initial={{
              y: 20,
              opacity: 0,
            }}
            animate={{
              y: 0,
              opacity: 1,
            }}
            exit={{
              y: -20,
              opacity: 0,
            }}
            transition={{
              duration: 0.2,
              ease: "easeInOut",
            }}
          >
            <h3 className="testify-text-2xl testify-font-bold testify-dark:text-white testify-text-black">
              {testimonials[active].name}
            </h3>
            <p className="testify-text-sm testify-text-gray-500 testify-dark:text-neutral-500">
              {testimonials[active].designation}
            </p>
            <motion.p
              className="testify-text-lg testify-text-gray-500 testify-mt-8 testify-dark:text-neutral-300"
              dangerouslySetInnerHTML={{
                __html: truncateText(testimonials[active].quote, 200),
              }}
            />
          </motion.div>
          <div className="testify-flex testify-gap-4 testify-pt-12 md:testify-pt-0">
            <button
              onClick={handlePrev}
              className="testify-h-7 testify-w-7 testify-rounded-full testify-bg-gray-100 dark:testify-bg-neutral-800 testify-flex testify-items-center testify-justify-center testify-group/button"
            >
              <IconArrowLeft className="testify-h-5 testify-w-5 testify-text-black dark:testify-text-neutral-400 group-hover/button:testify-rotate-12 testify-transition-transform testify-duration-300" />
            </button>
            <button
              onClick={handleNext}
              className="testify-h-7 testify-w-7 testify-rounded-full testify-bg-gray-100 dark:testify-bg-neutral-800 testify-flex testify-items-center testify-justify-center testify-group/button"
            >
              <IconArrowRight className="testify-h-5 testify-w-5 testify-text-black dark:testify-text-neutral-400 group-hover/button:-testify-rotate-12 testify-transition-transform testify-duration-300" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
